import undoable from 'redux-undo'
import { BotFlow } from '@feedyou-ai/feedbot-schema'
import { combineReducers } from '@reduxjs/toolkit'

import { usersReducer } from './users'
import { domainsReducer } from './domains'
import { dockReducer } from '../store/dock'
import { copyReducer } from '../store/copy'
import { ssmlReducer } from '../store/ssml'
import { botNlpReducer } from '../store/botNlp'

import { overlayReducer } from '../store/overlay'
import { searchesReducer } from '../store/searches'
import { featuresReducer } from '../store/features'
import { botNamesReducer } from '../store/botNames'
import { appReducer as appPureReducer } from './app'
import { botFlowReducer as botFlowPureReducer } from './botFlow'
import { reducer as botFlowSliceReducer } from '../store/flow/flowSlice'

import { nlpEditorReducer } from '../store/nlpEditor'
import { visitStatisticsReducer } from '../store/visitStatistics/visitStatisticsSlice'
import { modalsSlice } from '../store/modals/modalsSlice'
import { dialogStepsReducer } from '../store/dialogSteps'
import { socketServerReducer } from '../store/socketServer'
import { selectedMutationsReducer } from '../store/selectedMutations'
import { nlpAnalyzerReducer } from '../store/nlpAnalyzer'
import { reducer as appSliceReducer } from '../store/app/appSlice'
import { deployBotReducer } from '../store/deployBot'
import { launchHostingReducer } from '../store/launchHosting'
import { serverApi } from '../store/serverApi'
import {
  appPersistor,
  commentPersistor,
  deployBotPersistor,
  dialogStepsPersistor,
  dockPersistor,
  featuresPersistor,
  serverApiPersistor,
  userPersistor,
  visitStatisticsPersistor,
  nlpEditorPersistor
} from '../store/reducerPersistors'
import { reducer as scrollContainerReducer } from '../store/scrollContainer/scrollContainerSlice'
import { reducer as playerInfoReducer } from '../store/playerInfo/playerInfoSlice'
import { stepCreationMenuReducer } from '../store/stepCreationMenu'
import { reducer as commentsReducer } from '../store/comment/commentsSlice'
import { bannersReducer } from '../store/banners'
import { userReducer } from '../store/user/userSlice'
import { devToolsReducer } from '../features/devTools/devToolsSlice'
import { sortObjectKeys } from '../lib/object/sortObjectKeys'

function filterActions({ type, isRealtime }: any) {
  return !type.includes('@@router') && typeof isRealtime === 'undefined'
}

// use both old pure Redux bot reducer and the new Redux Toolkit based nlpBotReducer in the same time to allow gradual migration
const botFlowReducer = (state: any, action: any): BotFlow =>
  //@ts-ignore
  botFlowPureReducer(botFlowSliceReducer(botNlpReducer(state, action), action), action)

const appReducer = (state: any, action: any): any =>
  appPureReducer(appSliceReducer(state, action) as any, action)

export function createRootReducer() {
  const reducers = {
    //@ts-ignore
    app: appPersistor(undoable(appReducer, { filter: filterActions })),
    //@ts-ignore
    flow: undoable(botFlowReducer, { filter: filterActions }),
    comments: commentPersistor(commentsReducer),
    dock: dockPersistor(dockReducer),
    overlay: overlayReducer,
    searches: searchesReducer,
    [serverApi.reducerPath]: serverApiPersistor(serverApi.reducer),
    user: userPersistor(userReducer),
    users: usersReducer,
    domains: domainsReducer,
    scrollContainer: scrollContainerReducer,
    playerInfo: playerInfoReducer,
    copy: copyReducer,
    modals: modalsSlice.reducer,
    features: featuresPersistor(featuresReducer),
    socketServer: socketServerReducer,
    nlpEditor: nlpEditorPersistor(nlpEditorReducer),
    dialogSteps: dialogStepsPersistor(dialogStepsReducer),
    stepCreationMenu: stepCreationMenuReducer,
    botNames: botNamesReducer,
    selectedMutations: selectedMutationsReducer,
    deployBot: deployBotPersistor(deployBotReducer),
    launchHosting: launchHostingReducer,
    nlpAnalyzer: nlpAnalyzerReducer,
    ssml: ssmlReducer,
    visitStatistics: visitStatisticsPersistor(visitStatisticsReducer),
    banners: bannersReducer,
    devTools: devToolsReducer
  }

  //@ts-ignore
  return combineReducers(sortObjectKeys(reducers))
}

export type RootState = ReturnType<ReturnType<typeof createRootReducer>>
export type RS = RootState
